import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getListeOf, getListeConfiePierre } from '../../actions';
import { connect } from 'react-redux';
import {
    TextField,
    Button,
    CircularProgress,
    Box,
    Grid,
    Paper,
    Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { createDescPierre, getTodaySqlFormat } from '../../helpers/functions';
import Axios from 'axios';
import { apiATC, meekroDB } from '../../config';
import { getOfs } from '../../selectors/ofsSelector';
import DeleteIcon from '@material-ui/icons/Delete';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        padding: theme.spacing(4),
    },
    paper: {
        padding: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxShadow: theme.shadows[3],
        borderRadius: theme.shape.borderRadius * 2,
        backgroundColor: '#FFFFFF',
    },
    formControl: {
        marginBottom: theme.spacing(3),
        width: '100%',
    },
    buttonProgress: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -10,
        marginLeft: -10,
    },
    autocomplete: {
        width: '100%',
    },
    tableContainer: {
        maxHeight: 500,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
    },
    tableHead: {
        backgroundColor: '#9AC8EB',
    },
    tableHeaderCell: {
        fontWeight: 'bold',
        color: theme.palette.getContrastText('#9AC8EB'),
    },
    tableRow: {
        '&:nth-of-type(odd)': {
            backgroundColor: '#B6D8F2',
        },
        '&:nth-of-type(even)': {
            backgroundColor: '#FFFFFF',
        },
    },
    actionButton: {
        minWidth: 120,
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[4],
        },
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        backgroundColor: '#5784BA',
        color: '#FFFFFF',
        '&:disabled': {
            backgroundColor: '#A0A0A0',
            color: '#E0E0E0',
        },
    },
    deleteIconColor: {
        color: '#3d5d83',
    },
    '@global': {
        '.MuiInputLabel-outlined.Mui-focused': {
            color: '#5784BA',
        },
        '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#5784BA',
        },
        // ✅ Alertes Snackbar (variant="filled")
        '.MuiAlert-filledSuccess': {
            backgroundColor: '#5784BA !important',
            color: '#FFFFFF !important',
        },
        '.MuiAlert-filledInfo': {
            backgroundColor: '#5784BA !important',
            color: '#FFFFFF !important',
        },
        '.MuiAlert-filledWarning': {
            backgroundColor: '#5784BA !important',
            color: '#FFFFFF !important',
        },
        '.MuiAlert-filledError': {
            backgroundColor: '#5784BA !important',
            color: '#FFFFFF !important',
        },
        '.MuiButton-containedPrimary': {
            backgroundColor: '#5784BA',
            '&:hover': {
                backgroundColor: '#9AC8EB',
            },
        },
        '.MuiButton-containedSecondary': {
            backgroundColor: '#F4CFDF',
            color: theme.palette.getContrastText('#F4CFDF'),
            '&:hover': {
                backgroundColor: '#B6D8F2',
            },
        },
        '.MuiTypography-h5': {
            color: '#5784BA',
        },
    },
}));

function ReceptionPierre({ getListeOf, getListeConfiePierre, listeOfs, listeConfie }) {
    const classes = useStyles();

    const [of, setOf] = useState(null);
    const [confie, setConfie] = useState(null);
    const [poids, setPoids] = useState('');
    const [pierresAlimentees, setPierresAlimentees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [poidsError, setPoidsError] = useState(false);

    // For undo functionality
    const [deletedItem, setDeletedItem] = useState(null);
    const deleteTimeoutId = useRef(null);

    const ofInputRef = useRef(null);
    const confieInputRef = useRef(null);
    const poidsInputRef = useRef(null);

    useEffect(() => {
        getListeOf();
        getListeConfiePierre();
        if (ofInputRef.current) {
            ofInputRef.current.focus();
        }
    }, [getListeOf, getListeConfiePierre]);

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
        // If snackbar closes naturally, clear any pending permanent deletion
        if (deleteTimeoutId.current) {
            clearTimeout(deleteTimeoutId.current);
            deleteTimeoutId.current = null;
            setDeletedItem(null); // Clear deleted item after snackbar close
        }
    };

    const resetForm = useCallback(() => {
        setConfie(null);
        setPoids('');
        setPoidsError(false);
        if (ofInputRef.current) {
            ofInputRef.current.focus();
        }
    }, []);

    const selectOf = useCallback(async (selectedOf) => {
        setOf(selectedOf);
        setPierresAlimentees([]);
        if (!selectedOf) {
            return;
        }

        setLoading(true);
        try {
            const response = await Axios.get(`${meekroDB}/pierre/alimentee.php?of=${selectedOf.num_sachet}`);
            setPierresAlimentees(response.data || []);
            if (confieInputRef.current) {
                confieInputRef.current.focus();
            }
        } catch (error) {
            console.error("Erreur lors de la récupération des pierres alimentées:", error);
            showSnackbar("Erreur lors du chargement des pierres alimentées.", "error");
            setPierresAlimentees([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSave = async () => {
        if (!of || !confie || poids === '') {
            showSnackbar("Veuillez remplir tous les champs obligatoires (OF, Id Confié, Poids).", "warning");
            return;
        }

        const parsedPoids = parseFloat(poids);
        if (isNaN(parsedPoids) || parsedPoids <= 0) {
            setPoidsError(true);
            showSnackbar("Le poids doit être un nombre positif valide.", "warning");
            if (poidsInputRef.current) {
                poidsInputRef.current.focus();
            }
            return;
        }
        setPoidsError(false);

        setLoading(true);
        const data = {
            table: 'op_stock',
            date_op: getTodaySqlFormat(),
            poids_sortant: parsedPoids,
            of: of.num_sachet,
            id_stock: confie.id_conf,
        };

        try {
            await Axios.post(`${meekroDB}/pierre/pierre_conso_remove.php`, data);
            await Axios.post(`${apiATC}/general.php?action=ajouter`, data);

            showSnackbar("Opération enregistrée avec succès !", "success");
            selectOf(of);
            resetForm();
        } catch (error) {
            console.error("Erreur lors de l'enregistrement des données:", error);
            showSnackbar("Erreur lors de l'enregistrement de l'opération.", "error");
        } finally {
            setLoading(false);
        }
    };

    const deletePierreAlimenteePermanently = async (item) => {
        const deleteData = {
            table: 'op_stock',
            primaryKey: 'id_op',
            id: item.id_op,
        };
        try {
            await Axios.post(`${meekroDB}/general_remove.php`, deleteData);
        } catch (error) {
            console.error("Erreur lors de la suppression permanente des données:", error);
            showSnackbar("Erreur lors de la suppression finale de la pierre alimentée.", "error");
        }
    };

    const handleDeleteWithUndo = (itemToDelete) => {
        // Clear any existing undo timeout
        if (deleteTimeoutId.current) {
            clearTimeout(deleteTimeoutId.current);
        }

        // Optimistically remove the item from the UI
        setPierresAlimentees((prevPierres) =>
            prevPierres.filter((pierre) => pierre.id_op !== itemToDelete.id_op)
        );

        setDeletedItem(itemToDelete);
        showSnackbar("Pierre alimentée supprimée.", "info");

        // Set a timeout to permanently delete the item after a delay
        deleteTimeoutId.current = setTimeout(() => {
            deletePierreAlimenteePermanently(itemToDelete);
            setDeletedItem(null); // Clear deleted item after permanent deletion
        }, 5000); // 5 seconds to undo
    };

    const handleUndoDelete = () => {
        // Clear the timeout to prevent permanent deletion
        if (deleteTimeoutId.current) {
            clearTimeout(deleteTimeoutId.current);
            deleteTimeoutId.current = null;
        }

        // Only restore if there's actually an item that was "deleted"
        if (deletedItem) {
            setPierresAlimentees((prevPierres) => [...prevPierres, deletedItem]);
            showSnackbar("Suppression annulée. Pierre restaurée.", "success");
        } else {
            // For other snackbars that just have an "Annuler" option
            showSnackbar("Action annulée.", "info");
        }
        setDeletedItem(null); // Clear the deleted item regardless
        setSnackbarOpen(false); // Close the snackbar
    };

    const handleKeyPressForNavigation = useCallback((event, nextRef) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (nextRef && nextRef.current) {
                if (nextRef === ofInputRef || nextRef === confieInputRef) {
                    const input = nextRef.current.querySelector('input');
                    if (input) {
                        input.focus();
                    }
                } else {
                    nextRef.current.focus();
                }
            }
        }
    }, []);

    const isSaveButtonDisabled = !of || !confie || poids === '' || isNaN(parseFloat(poids)) || parseFloat(poids) <= 0 || loading;

    return (
        <div className={classes.root}>
            <Grid container spacing={4}>
                {/* Form Section */}
                <Grid item xs={12} md={4}>
                    <Paper className={classes.paper}>
                        <Typography variant="h5" component="h2" gutterBottom align="center">
                            Nouvelle Alimentation de Pierre
                        </Typography>
                        <Box my={3}>
                            <Autocomplete
                                value={of}
                                options={listeOfs}
                                getOptionLabel={(option) => option.num_sachet || ''}
                                onChange={(e, values) => {
                                    setOf(values);
                                    if (values) {
                                        selectOf(values);
                                    } else {
                                        setPierresAlimentees([]);
                                        resetForm();
                                    }
                                }}
                                autoHighlight
                                autoComplete
                                clearOnEscape
                                className={classes.autocomplete}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Numéro OF"
                                        variant="outlined"
                                        inputRef={ofInputRef}
                                        onKeyPress={(e) => handleKeyPressForNavigation(e, confieInputRef)}
                                    />
                                )}
                            />
                        </Box>
                        <Box my={3}>
                            <Autocomplete
                                value={confie}
                                options={listeConfie}
                                getOptionLabel={(option) => option.id_conf ? String(option.id_conf) : ''}
                                onChange={(event, values) => {
                                    setConfie(values);
                                    if (values && poidsInputRef.current) {
                                        poidsInputRef.current.focus();
                                    }
                                }}
                                autoHighlight
                                autoComplete
                                clearOnEscape
                                className={classes.autocomplete}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        label="Id Confié"
                                        variant="outlined"
                                        inputRef={confieInputRef}
                                        onKeyPress={(e) => handleKeyPressForNavigation(e, poidsInputRef)}
                                    />
                                )}
                            />
                        </Box>
                        <Box my={3}>
                            <TextField
                                label="Poids (carats)"
                                variant="outlined"
                                value={poids}
                                onChange={(e) => {
                                    setPoids(e.target.value);
                                    if (poidsError && parseFloat(e.target.value) > 0) {
                                        setPoidsError(false);
                                    }
                                }}
                                inputRef={poidsInputRef}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSave();
                                    }
                                }}
                                type="number"
                                error={poidsError}
                                helperText={poidsError ? "Le poids doit être un nombre positif valide." : ""}
                                className={classes.autocomplete}
                            />
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={isSaveButtonDisabled}
                            className={classes.actionButton}
                            style={{ position: 'relative' }}
                        >
                            Enregistrer l'Opération
                            {loading && <CircularProgress size={20} className={classes.buttonProgress} />}
                        </Button>
                    </Paper>
                </Grid>

                {/* Table Section */}
                <Grid item xs={12} md={8}>
                    <Paper className={classes.paper}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h5" component="h2">
                                Pierres Alimentées pour OF: {of ? of.num_sachet : 'Non sélectionné'}
                            </Typography>
                            {loading && <CircularProgress size={20} />}
                        </Box>

                        <TableContainer className={classes.tableContainer}>
                            <Table stickyHeader aria-label="custom table">
                                <TableHead className={classes.tableHead}>
                                    <TableRow>
                                        <TableCell className={classes.tableHeaderCell}>Id Confie</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>N° Confie</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Description</TableCell>
                                        <TableCell className={classes.tableHeaderCell} align="right">Reçu</TableCell>
                                        <TableCell className={classes.tableHeaderCell} align="right">Consommé</TableCell>
                                        <TableCell className={classes.tableHeaderCell} align="right">Euro</TableCell>
                                        <TableCell className={classes.tableHeaderCell} align="right">Dollar</TableCell>
                                        <TableCell className={classes.tableHeaderCell} align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pierresAlimentees.length === 0 && !loading ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center">
                                                Aucune pierre alimentée pour cet OF.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        pierresAlimentees.map((row) => (
                                            <TableRow key={row.id_op} className={classes.tableRow}>
                                                <TableCell>{row.id_conf}</TableCell>
                                                <TableCell>{row.no_confie}</TableCell>
                                                <TableCell>{createDescPierre(row)}</TableCell>
                                                <TableCell align="right">{row.poids}</TableCell>
                                                <TableCell align="right">{row.totconso}</TableCell>
                                                <TableCell align="right">{row.euro}</TableCell>
                                                <TableCell align="right">{row.dollar}</TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        aria-label="delete"
                                                        onClick={() => handleDeleteWithUndo(row)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="small" className={classes.deleteIconColor} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Snackbar for user feedback with Undo option */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    elevation={6}
                    variant="filled"
                    // Always show the "Annuler" button
                    action={
                        <Button color="inherit" size="small" onClick={handleUndoDelete}>
                            Annuler
                        </Button>
                    }
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}

const mapStateToProps = (state) => {
    return {
        listeOfs: getOfs(state),
        listeConfie: state.pierre.listeConfiePierre,
    };
};

const mapDispatchToProps = {
    getListeOf,
    getListeConfiePierre,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReceptionPierre);